'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/lib/supabase/auth-context';
import { createClient } from '@/lib/supabase/client';

interface HeaderProps {
  className?: string;
}

interface Notification {
  id: string;
  type: 'inquiry' | 'product' | 'system';
  title: string;
  message: string;
  time: string;
  created_at: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { user, loading, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [language, setLanguage] = useState('en');
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);


  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle('dark', savedTheme === 'dark');
    }

    // Check system preference if no saved theme
    if (!savedTheme) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        setTheme('dark');
        document.documentElement.classList.add('dark');
      }
    }
  }, []);

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);

      // Apply RTL for Arabic
      if (savedLanguage === 'ar') {
        document.documentElement.setAttribute('dir', 'rtl');
        document.documentElement.setAttribute('lang', 'ar');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
        document.documentElement.setAttribute('lang', savedLanguage);
      }
    }
  }, []);

  // Fetch notifications for logged-in owners
  useEffect(() => {
    if (!user) return;

    const fetchNotifications = async () => {
      const supabase = createClient();
      const role = user.user_metadata?.role;

      if (role === 'owner') {
        // Get manufacturer ID
        const { data: manufacturer } = await supabase
          .from('manufacturers')
          .select('id')
          .eq('user_id', user.id)
          .single();

        if (manufacturer) {
          // Fetch recent inquiries as notifications
          const { data: inquiries } = await supabase
            .from('inquiries')
            .select('*, products(name)')
            .eq('manufacturer_id', manufacturer.id)
            .order('created_at', { ascending: false })
            .limit(5);

          if (inquiries) {
            const notifs: Notification[] = inquiries.map(inq => ({
              id: inq.id,
              type: 'inquiry' as const,
              title: 'New inquiry received',
              message: `${inq.products?.name || 'Product'} - ${inq.quantity || 'N/A'}`,
              time: getTimeAgo(inq.created_at),
              created_at: inq.created_at
            }));
            setNotifications(notifs);
          }
        }
      }
    };

    fetchNotifications();
  }, [user]);

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  const handleDeleteNotification = (id: string) => {
    setNotifications(notifications.filter(n => n.id !== id));
  };

  const handleMarkAllRead = () => {
    // In a real app, this would update the database
    alert('All notifications marked as read');
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const changeLanguage = (lang: string) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    setIsLanguageMenuOpen(false);
    // Trigger custom event for same-tab updates
    window.dispatchEvent(new Event('languageChange'));

    // Apply RTL for Arabic
    if (lang === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', lang);
    }
  };

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'ar', name: 'العربية', flag: '🇪🇬' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  const primaryNavItems = [
    { label: t('home'), href: '/homepage', icon: 'HomeIcon' },
    { label: t('manufacturers'), href: '/manufacturer-directory', icon: 'BuildingOfficeIcon' },
    { label: t('products'), href: '/product-catalog', icon: 'CubeIcon' },
    { label: t('blog'), href: '/blog', icon: 'DocumentTextIcon' },
  ];

  // Only show dashboard in secondary nav if user is not logged in
  const secondaryNavItems = !user ? [
    { label: t('dashboard'), href: '/owner-signin', icon: 'ChartBarIcon' },
  ] : [];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsMoreMenuOpen(false);
  };

  const toggleMoreMenu = () => {
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };

  const toggleLanguageMenu = () => {
    setIsLanguageMenuOpen(!isLanguageMenuOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const handleSignOut = async () => {
    await signOut();
    setIsProfileMenuOpen(false);
    router.push('/homepage');
    router.refresh();
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
  };

  const getUserRole = () => {
    if (!user) return '';
    const role = user.user_metadata?.role;
    if (role === 'owner') return 'Owner';
    if (role === 'manufacturer') return 'Manufacturer';
    return 'Visitor';
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-[100] ${className}`}>
      {/* Egyptian Heritage Glassmorphic Background with Gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-50/95 via-stone-50/90 to-amber-100/95 dark:from-stone-900/95 dark:via-amber-950/90 dark:to-stone-900/95 backdrop-blur-xl border-b border-amber-200/30 dark:border-amber-800/30 shadow-lg shadow-amber-500/10 dark:shadow-amber-900/20"></div>

      {/* Egyptian Gold Accent Top Gradient Line */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-amber-500/40 dark:via-amber-600/50 to-transparent"></div>

      <div className="relative w-full pointer-events-auto z-10">
        <div className="flex items-center justify-between h-16 px-6 max-w-[1400px] mx-auto">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center space-x-2.5 flex-shrink-0 group">
            <div className="relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-105">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl blur-md opacity-0 group-hover:opacity-30 dark:group-hover:opacity-20 transition-opacity duration-300"></div>
              <img
                src="/logo.png"
                alt="EgypTex Gateway Logo"
                className="w-9 h-9 object-contain relative z-10"
              />
            </div>
            <span className="text-lg font-bold bg-gradient-to-r from-stone-900 via-amber-800 to-stone-900 dark:from-amber-100 dark:via-amber-200 dark:to-amber-100 bg-clip-text text-transparent group-hover:from-amber-700 group-hover:via-amber-600 group-hover:to-orange-700 dark:group-hover:from-amber-300 dark:group-hover:via-amber-200 dark:group-hover:to-orange-300 transition-all duration-300">
              EgypTex Gateway
            </span>
          </Link>

          {/* Primary Navigation */}
          <nav className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
            {primaryNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group relative flex items-center space-x-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 transition-all duration-300 hover:scale-105"
              >
                {/* Hover background with gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-900/20 dark:via-orange-900/20 dark:to-amber-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {/* Subtle border on hover */}
                <div className="absolute inset-0 border border-amber-200/50 dark:border-amber-700/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <Icon name={item.icon as any} size={18} variant="outline" className="relative z-10 group-hover:scale-110 transition-transform duration-300" />
                <span className="relative z-10">{item.label}</span>
              </Link>
            ))}

            {/* Show More dropdown when not logged in, Help Center link when logged in */}
            {!user ? (
              <div className="relative">
                <button
                  onClick={toggleMoreMenu}
                  className="group relative flex items-center space-x-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 transition-all duration-300 hover:scale-105"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-900/20 dark:via-orange-900/20 dark:to-amber-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute inset-0 border border-amber-200/50 dark:border-amber-700/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Icon name="EllipsisHorizontalIcon" size={18} variant="outline" className="relative z-10 group-hover:scale-110 transition-transform duration-300" />
                  <span className="relative z-10">{t('more')}</span>
                </button>

                {isMoreMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-amber-50/95 dark:bg-stone-800/95 backdrop-blur-xl border border-amber-200/50 dark:border-amber-800/50 rounded-xl shadow-2xl shadow-amber-500/10 dark:shadow-amber-900/20 overflow-hidden z-[110]">
                    {/* Gradient top border */}
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500"></div>
                    {secondaryNavItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setIsMoreMenuOpen(false)}
                        className="group flex items-center space-x-3 px-4 py-3 text-sm font-semibold text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-gradient-to-r hover:from-amber-100 hover:to-orange-50 dark:hover:from-amber-900/30 dark:hover:to-orange-900/30 transition-all duration-200 first:rounded-t-xl last:rounded-b-xl"
                      >
                        <Icon name={item.icon as any} size={18} variant="outline" className="group-hover:scale-110 transition-transform duration-200" />
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                href="/help-center"
                className="group relative flex items-center space-x-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 transition-all duration-300 hover:scale-105"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-900/20 dark:via-orange-900/20 dark:to-amber-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 border border-amber-200/50 dark:border-amber-700/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <Icon name="QuestionMarkCircleIcon" size={18} variant="outline" className="relative z-10 group-hover:scale-110 transition-transform duration-300" />
                <span className="relative z-10">Help Center</span>
              </Link>
            )}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center space-x-2">
            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={toggleLanguageMenu}
                className="group relative flex items-center space-x-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 transition-all duration-300 hover:scale-105"
                aria-label="Change language"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-900/20 dark:via-orange-900/20 dark:to-amber-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 border border-amber-200/50 dark:border-amber-700/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="text-base relative z-10">{currentLanguage.flag}</span>
                <span className="hidden xl:inline text-xs font-bold relative z-10">{currentLanguage.code.toUpperCase()}</span>
                <Icon name="ChevronDownIcon" size={14} variant="outline" className="relative z-10 group-hover:rotate-180 transition-transform duration-300" />
              </button>

              {isLanguageMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-amber-50/95 dark:bg-stone-800/95 backdrop-blur-xl border border-amber-200/50 dark:border-amber-800/50 rounded-xl shadow-2xl shadow-amber-500/10 dark:shadow-amber-900/20 overflow-hidden z-[110]">
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500"></div>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`group w-full flex items-center space-x-3 px-4 py-3 text-sm font-semibold transition-all duration-200 ${language === lang.code
                        ? 'bg-gradient-to-r from-amber-100 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 text-amber-700 dark:text-amber-300'
                        : 'text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-gradient-to-r hover:from-amber-50/50 hover:to-orange-50/50 dark:hover:from-amber-900/10 dark:hover:to-orange-900/10'
                        }`}
                    >
                      <span className="text-base group-hover:scale-110 transition-transform duration-200">{lang.flag}</span>
                      <span className="flex-1 text-left">{lang.name}</span>
                      {language === lang.code && (
                        <Icon name="CheckIcon" size={16} variant="outline" className="text-amber-700 dark:text-amber-400 animate-pulse" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="group relative p-2.5 rounded-xl text-gray-700 dark:text-gray-300 hover:text-amber-600 dark:hover:text-amber-400 transition-all duration-300 hover:scale-105"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 dark:from-amber-900/20 dark:via-yellow-900/20 dark:to-amber-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="absolute inset-0 border border-amber-200/50 dark:border-amber-700/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Icon
                name={theme === 'light' ? 'MoonIcon' : 'SunIcon'}
                size={20}
                variant="outline"
                className="relative z-10 group-hover:rotate-12 transition-transform duration-300"
              />
            </button>

            {/* Notifications - Only for logged in users */}
            {!loading && user && (
              <div className="relative">
                <button
                  onClick={() => setIsNotificationMenuOpen(!isNotificationMenuOpen)}
                  className="group relative p-2.5 rounded-xl text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 transition-all duration-300 hover:scale-105"
                  aria-label="Notifications"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-900/20 dark:via-orange-900/20 dark:to-amber-900/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute inset-0 border border-amber-200/50 dark:border-amber-700/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Icon name="BellIcon" size={20} variant="outline" className="relative z-10 group-hover:rotate-12 transition-transform duration-300" />
                  {/* Notification badge - only show if there are notifications */}
                  {notifications.length > 0 && (
                    <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-gradient-to-r from-red-500 to-red-600 rounded-full ring-2 ring-white dark:ring-gray-900 animate-pulse"></span>
                  )}
                </button>

                {isNotificationMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[110]">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
                      {notifications.length > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.length > 0 ? (
                        <div className="py-1">
                          {notifications.map((notification) => (
                            <div key={notification.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group">
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${notification.type === 'inquiry' ? 'bg-blue-100 dark:bg-blue-900/30' :
                                  notification.type === 'product' ? 'bg-green-100 dark:bg-green-900/30' :
                                    'bg-purple-100 dark:bg-purple-900/30'
                                  }`}>
                                  <Icon
                                    name={
                                      notification.type === 'inquiry' ? 'ChatBubbleLeftRightIcon' :
                                        notification.type === 'product' ? 'CheckCircleIcon' :
                                          'InformationCircleIcon'
                                    }
                                    size={16}
                                    variant="outline"
                                    className={
                                      notification.type === 'inquiry' ? 'text-blue-600 dark:text-blue-400' :
                                        notification.type === 'product' ? 'text-green-600 dark:text-green-400' :
                                          'text-purple-600 dark:text-purple-400'
                                    }
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">{notification.title}</p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{notification.message}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{notification.time}</p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteNotification(notification.id);
                                  }}
                                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-all"
                                >
                                  <Icon name="XMarkIcon" size={14} variant="outline" className="text-red-600 dark:text-red-400" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-12 text-center">
                          <Icon name="BellIcon" size={48} variant="outline" className="text-gray-400 dark:text-gray-600 mx-auto mb-3 opacity-50" />
                          <p className="text-sm text-gray-600 dark:text-gray-400">No notifications</p>
                        </div>
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                        <Link
                          href="/inquiry-management-system"
                          onClick={() => setIsNotificationMenuOpen(false)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium w-full text-center block"
                        >
                          View all notifications
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* User Profile or Sign In */}
            {!loading && (
              user ? (
                <div className="relative ml-2">
                  <button
                    onClick={toggleProfileMenu}
                    className="flex items-center space-x-2 px-2.5 py-1.5 rounded-lg text-sm font-medium text-stone-900 dark:text-amber-100 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all duration-200"
                  >
                    {user.user_metadata?.avatar_url ? (
                      <img
                        src={user.user_metadata.avatar_url}
                        alt={getUserDisplayName()}
                        className="w-8 h-8 rounded-full shadow-md object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-amber-600 to-orange-600 text-white rounded-full flex items-center justify-center font-semibold text-sm shadow-md">
                        {getUserDisplayName().charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="hidden xl:block text-left">
                      <div className="text-sm font-semibold leading-tight">{getUserDisplayName()}</div>
                      <div className="text-xs text-stone-600 dark:text-amber-200/70 leading-tight">{getUserRole()}</div>
                    </div>
                    <Icon name="ChevronDownIcon" size={14} variant="outline" className="text-stone-600 dark:text-amber-200/70" />
                  </button>

                  {isProfileMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-amber-50/95 dark:bg-stone-800/95 backdrop-blur-xl border border-amber-200/50 dark:border-amber-800/50 rounded-xl shadow-2xl shadow-amber-500/10 dark:shadow-amber-900/20 overflow-hidden z-[110]">
                      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500"></div>
                      <div className="px-4 py-3 border-b border-amber-200/50 dark:border-amber-800/50">
                        <div className="text-sm font-semibold text-stone-900 dark:text-amber-100">{getUserDisplayName()}</div>
                        <div className="text-xs text-stone-600 dark:text-amber-200/70 mt-0.5">{user.email}</div>
                        <div className="text-xs text-amber-700 dark:text-amber-400 mt-1 font-medium">{getUserRole()}</div>
                      </div>
                      <div className="py-1">
                        <Link
                          href="/my-account"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2.5 text-sm text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-100/50 dark:hover:bg-amber-900/20 transition-colors"
                        >
                          <Icon name="UserCircleIcon" size={16} variant="outline" />
                          <span>My Account</span>
                        </Link>
                        {getUserRole() === 'Owner' && (
                          <Link
                            href="/vendor-dashboard"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2.5 text-sm text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-100/50 dark:hover:bg-amber-900/20 transition-colors"
                          >
                            <Icon name="ChartBarIcon" size={16} variant="outline" />
                            <span>Dashboard</span>
                          </Link>
                        )}
                        {getUserRole() === 'Owner' ? (
                          <Link
                            href="/inquiry-management-system"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2.5 text-sm text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-100/50 dark:hover:bg-amber-900/20 transition-colors"
                          >
                            <Icon name="EnvelopeIcon" size={16} variant="outline" />
                            <span>Inquiries</span>
                          </Link>
                        ) : (
                          <Link
                            href="/product-catalog"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center space-x-3 px-4 py-2.5 text-sm text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-100/50 dark:hover:bg-amber-900/20 transition-colors"
                          >
                            <Icon name="CubeIcon" size={16} variant="outline" />
                            <span>Browse Products</span>
                          </Link>
                        )}
                        <div className="border-t border-amber-200/50 dark:border-amber-800/50 my-1"></div>
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/signin"
                  className="group relative ml-2 px-6 py-2.5 text-sm font-bold text-white overflow-hidden rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/40 dark:hover:shadow-amber-600/30"
                >
                  {/* Egyptian Gold Gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-600 via-amber-500 to-orange-600 group-hover:from-amber-500 group-hover:via-orange-500 group-hover:to-amber-600 transition-all duration-300"></div>
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                  <span className="relative z-10">Sign In</span>
                </Link>
              )
            )}
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 rounded-xl text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden bg-amber-50/95 dark:bg-stone-900/95 backdrop-blur-xl border-t border-amber-200/50 dark:border-amber-800/50">
            <nav className="px-4 py-4 space-y-1">
              {[...primaryNavItems, ...secondaryNavItems].map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors duration-200"
                >
                  <Icon name={item.icon as any} size={20} variant="outline" />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>

            <div className="px-4 py-4 border-t border-amber-200/50 dark:border-amber-800/50 space-y-2">
              {/* Mobile Language Selector */}
              <div className="mb-3">
                <label className="text-xs font-semibold text-stone-600 dark:text-amber-200/70 uppercase mb-2 block">
                  {t('language')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`flex items-center justify-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${language === lang.code
                        ? 'bg-gradient-to-r from-amber-600 to-orange-600 text-white shadow-md'
                        : 'bg-amber-100 dark:bg-amber-900/20 text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300'
                        }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.code.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="w-full flex items-center justify-between px-4 py-3 bg-amber-100 dark:bg-amber-900/20 rounded-xl text-sm font-medium text-stone-700 dark:text-amber-100 hover:text-amber-700 dark:hover:text-amber-300 transition-colors duration-200"
              >
                <span>{t('theme')}</span>
                <div className="flex items-center space-x-2">
                  <Icon
                    name={theme === 'light' ? 'SunIcon' : 'MoonIcon'}
                    size={18}
                    variant="outline"
                  />
                  <span className="text-xs capitalize">{theme}</span>
                </div>
              </button>

              {/* Mobile Auth Buttons */}
              {!loading && (
                user ? (
                  <div className="space-y-2">
                    <div className="px-4 py-3 bg-gradient-to-r from-amber-100 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 rounded-xl">
                      <div className="flex items-center space-x-3">
                        {user.user_metadata?.avatar_url ? (
                          <img
                            src={user.user_metadata.avatar_url}
                            alt={getUserDisplayName()}
                            className="w-10 h-10 rounded-full shadow-md object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-amber-600 to-orange-600 text-white rounded-full flex items-center justify-center font-semibold shadow-md">
                            {getUserDisplayName().charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-semibold text-stone-900 dark:text-amber-100">{getUserDisplayName()}</div>
                          <div className="text-xs text-stone-600 dark:text-amber-200/70">{user.email}</div>
                          <div className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">{getUserRole()}</div>
                        </div>
                      </div>
                    </div>
                    <Link
                      href="/my-account"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full inline-block text-center px-4 py-2.5 bg-amber-100 dark:bg-amber-900/30 text-stone-700 dark:text-amber-100 rounded-xl text-sm font-semibold hover:bg-amber-200 dark:hover:bg-amber-900/40 transition-colors duration-200"
                    >
                      My Account
                    </Link>
                    {getUserRole() === 'Owner' ? (
                      <Link
                        href="/vendor-dashboard"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full inline-block text-center px-4 py-2.5 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl text-sm font-semibold hover:from-amber-500 hover:to-orange-500 transition-all duration-200 shadow-md"
                      >
                        Go to Dashboard
                      </Link>
                    ) : (
                      <Link
                        href="/product-catalog"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full inline-block text-center px-4 py-2.5 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl text-sm font-semibold hover:from-amber-500 hover:to-orange-500 transition-all duration-200 shadow-md"
                      >
                        Browse Products
                      </Link>
                    )}
                    <button
                      onClick={handleSignOut}
                      className="w-full px-4 py-2.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl text-sm font-semibold hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200"
                    >
                      Sign Out
                    </button>
                  </div>
                ) : (
                  <Link
                    href="/signin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full inline-block text-center px-4 py-2.5 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl text-sm font-semibold hover:from-amber-500 hover:to-orange-500 transition-all duration-200 shadow-md"
                  >
                    Sign In
                  </Link>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
